import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";

import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { RecoilRoot } from 'recoil';
import posthog from 'posthog-js';
import { getAdminToken } from './features/admin/api/adminApi';
import { APP_SESSION_KEY } from './lib/appNav';

// 앱(Play 스토어 TWA) 실행 감지(2026-10-07) — TWA는 start_url에 ?source=twa를 붙여 열고, 첫 로드의
// referrer가 android-app://로 시작함. 이후 페이지 이동에선 둘 다 사라지므로 sessionStorage에 기억.
// manifest.json의 웹 PWA 설치는 ?source=pwa라 앱으로 치지 않음(웹과 동일 취급).
// 앱에서는 세션 리플레이(화면 녹화)를 끔 — 스토어 데이터 보안 양식·개인정보처리방침 부담을 줄이기 위함.
// (키 이름은 lib/appNav.ts와 공유 — 앱 모드 판정에도 쓰임)
let appPlatform: string | null = null;
try {
  const params = new URLSearchParams(window.location.search);
  if (params.get('source') === 'twa' || document.referrer.startsWith('android-app://')) {
    sessionStorage.setItem(APP_SESSION_KEY, 'android_twa');
  }
  appPlatform = sessionStorage.getItem(APP_SESSION_KEY);
  // 공유 링크에 source 파라미터가 묻어나가지 않게 주소창에서 제거
  if (params.has('source')) {
    params.delete('source');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
  }
} catch {}

posthog.init('phc_m87KSNiiH2nGjqJ28dr5fiJxLWXxJRqCTz9zYLuLnajE', {
  disable_session_recording: appPlatform !== null,
  api_host: 'https://us.i.posthog.com',
  person_profiles: 'identified_only',
  // 라우터 없이 pushState로 주소만 바꾸는 화면(오프닝북 등)도 페이지뷰로 잡히게(2026-10-03) — 기본값은
  // 첫 로드만 세서, 30일 페이지뷰가 GA4(2,842)보다 약 900건 적게(1,942) 집계되고 있었음
  capture_pageview: 'history_change',
  capture_exceptions: true, // 처리 안 된 JS 에러/프로미스 거부 자동 캡처 (2026-09-03 활성화, 그동안 꺼져있었음)
});

// 본인 트래픽 표시(_self_traffic, index.html이 ?self=1·어드민 로그인 때 남김)가 있는 브라우저는 PostHog에도 is_admin(2026-10-23)
try {
  if (localStorage.getItem('_self_traffic') === '1') posthog.register({ is_admin: true });
  // 반대로 예전에 is_admin이 붙은 브라우저(어드민 로그인 이력)는 GA 표시도 남긴다 — 다음 방문부터 GA에서 빠짐
  else if (posthog.get_property('is_admin')) localStorage.setItem('_self_traffic', '1');
} catch {}

// 앱 유입을 웹과 구분해 집계할 수 있게 모든 이벤트에 app_platform 부착(PostHog super property, GA4 user property)
if (appPlatform) {
  posthog.register({ app_platform: appPlatform });
  (window as any).gtag?.('set', 'user_properties', { app_platform: appPlatform });
}

// 이력서(Battlecode 결승 자격용) 경유 방문 마킹(2026-09-27) — 채용 담당자·심사위원일 가능성이 높은
// 트래픽이라 놓치면 안 됨. is_admin 마킹(adminApi.ts)과 동일 패턴: register()로 super property를
// 심어 이후 이 브라우저의 모든 이벤트에 항상 붙게 함(person_profiles: identified_only여도 이벤트
// 속성으로는 남아서 세션 리플레이 목록을 traffic_source=resume으로 필터링 가능). 사이트 전체 세션
// 리플레이 샘플링에 걸려 녹화가 빠질 가능성을 아예 없애기 위해 startSessionRecording()도 강제 호출.
// GA4는 세션 리플레이(화면 녹화) 기능 자체가 없음 — user_properties로 세그먼트만 나눠볼 수 있게.
//
// 본인 트래픽 제외: 이 링크는 본인도 테스트 삼아 계속 누르게 될 텐데, 그게 "지원자 트래픽"으로
// 잘못 잡히면 안 됨. admin 로그인 중이면 자동으로 걸러지고, 로그인 없이 그냥 눌러볼 땐 링크에
// &self=1을 붙이면 is_admin으로 마킹해 기존 3단계 본인 트래픽 제외 메커니즘(project_analytics.md)에
// 그대로 태운다 — IP는 유동적이고 커밋된 코드에 박아둘 개인정보라 IP 하드코딩 대신 이 방식을 택함.
// PostHog 프로젝트 설정의 "Internal and test accounts"에 본인 IP를 등록해두면 이 마킹과 별개로
// 대시보드 집계에서도 영구 제외되니 추가로 해두는 걸 권장(코드로는 설정 불가, 대시보드 전용).
try {
  const params = new URLSearchParams(window.location.search);
  const isSelf = params.get('self') === '1' || !!getAdminToken();
  if (params.get('utm_source') === 'resume') {
    if (isSelf) {
      posthog.register({ is_admin: true });
    } else {
      posthog.register({ traffic_source: 'resume', resume_lang: params.get('utm_content') ?? undefined });
      posthog.startSessionRecording();
      (window as any).gtag?.('set', 'user_properties', { traffic_source: 'resume' });
      // user_properties는 소급 적용이라 GA4 실시간/이벤트 리포트에 바로 안 뜰 수 있음 — 방문
      // 즉시 눈에 띄는 이벤트로도 하나 쏴둔다(2026-09-28).
      (window as any).gtag?.('event', 'resume_visit', {
        resume_lang: params.get('utm_content') ?? undefined,
        campaign: params.get('utm_campaign') ?? undefined,
      });

      // 세션 요약 롤업(2026-09-28) — 개별 이벤트를 일일이 안 열어봐도 "얼마나 머물렀는지,
      // 검색을 했는지, 강의를 열어봤는지, GitHub 링크를 눌렀는지"를 한 줄로 보기 위함. posthog.capture
      // 를 얇게 감싸서 이 세션에서 나가는 모든 이벤트 이름을 세기만 하고 원래 동작은 그대로 통과시킴
      // — traffic_source=resume 세션에서만 걸리므로 일반 사용자 트래픽엔 영향 없음.
      const sessionStart = Date.now();
      const eventCounts: Record<string, number> = {};
      let clickedGithub = false;
      const originalCapture = posthog.capture.bind(posthog);
      posthog.capture = ((event: string, props?: Record<string, unknown>, options?: unknown) => {
        eventCounts[event] = (eventCounts[event] ?? 0) + 1;
        if (!clickedGithub && props && JSON.stringify(props).includes('github.com')) {
          clickedGithub = true;
        }
        return (originalCapture as any)(event, props, options);
      }) as typeof posthog.capture;

      const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8787';
      let summarySent = false;
      const sendSummary = () => {
        if (summarySent) return;
        summarySent = true;
        const durationMs = Date.now() - sessionStart;
        const pageviews = eventCounts['$pageview'] ?? 0;
        const searches = (eventCounts['search_query_fired'] ?? 0) + (eventCounts['search_completed'] ?? 0);
        const lecturesOpened = eventCounts['lecture_opened'] ?? 0;
        originalCapture('resume_session_summary', {
          duration_ms: durationMs,
          pageviews,
          searches,
          lectures_opened: lecturesOpened,
          clicked_github: clickedGithub,
          event_counts: eventCounts,
        });
        // Discord/Slack 실시간 알림(2026-09-28) — beacon으로 보내야 pagehide 시점에도 유실 없이
        // 전송됨(일반 fetch는 페이지 언로드 중 취소될 수 있음).
        try {
          const payload = JSON.stringify({
            durationMs,
            pageviews,
            searches,
            lecturesOpened,
            clickedGithub,
            lang: params.get('utm_content') ?? undefined,
          });
          navigator.sendBeacon?.(`${API_URL}/api/lectures/resume-alert`, new Blob([payload], { type: 'application/json' }));
        } catch {}
      };
      window.addEventListener('pagehide', sendSummary);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') sendSummary();
      });
    }
  }
} catch {}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// React Query 캐시는 기본적으로 메모리 전용이라 새로고침(F5)하면 다 날아가서, staleTime을
// 5분/1시간으로 길게 잡아둔 race-labels·tips도 매번 새로고침마다 재요청됐다(2026-09-17). 이
// localStorage persister는 자주 안 바뀌는 참조 데이터만 골라서(shouldDehydrateQuery) 지속시켜,
// 새로고침 후에도 캐시가 살아있게 한다 — 검색 결과처럼 자주 바뀌고 조합이 다양한 쿼리는 대상에서
// 제외(오히려 오래된 결과를 보여줄 위험이 큼).
const PERSISTED_QUERY_KEYS = ["raceLabels", "bwTips"];
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: "starinfo-query-cache",
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

root.render(
  <React.StrictMode>
    <RecoilRoot>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          dehydrateOptions: {
            shouldDehydrateQuery: (query) =>
              PERSISTED_QUERY_KEYS.includes(query.queryKey[0] as string),
          },
        }}
      >
        <App />
      </PersistQueryClientProvider>
    </RecoilRoot>
  </React.StrictMode>,
);
